import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronUp, CheckCircle2, Circle, Leaf, Sun, CloudRain, Snowflake, Search, Crown, Trash2, Droplets, PlusSquare, Bug, AlertTriangle, Wind, Package, Shield, Lock, MinusSquare, Scale, AlertCircle, Ear, Wrench, ShoppingCart, ClipboardList, Layers, RefreshCw, GitMerge, Plus, X, PartyPopper, ArrowRight, Settings } from 'lucide-react';
import { Toggle } from '../components/ui/toggle';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEASON_META = {
  spring: { label: 'Spring', icon: Leaf, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', accent: 'bg-green-600' },
  summer: { label: 'Summer', icon: Sun, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', accent: 'bg-amber-600' },
  autumn: { label: 'Autumn', icon: CloudRain, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', accent: 'bg-orange-600' },
  winter: { label: 'Winter', icon: Snowflake, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', accent: 'bg-blue-600' },
};

const ICON_MAP = {
  'search': Search, 'crown': Crown, 'trash': Trash2, 'alert-triangle': AlertTriangle,
  'droplets': Droplets, 'plus-square': PlusSquare, 'bug': Bug, 'refresh-cw': RefreshCw,
  'layers': Layers, 'wind': Wind, 'package': Package, 'shield': Shield,
  'lock': Lock, 'git-merge': GitMerge, 'minus-square': MinusSquare, 'scale': Scale,
  'alert-circle': AlertCircle, 'ear': Ear, 'tool': Wrench, 'shopping-cart': ShoppingCart,
  'clipboard': ClipboardList,
};

const PRIORITY_COLORS = {
  high: 'text-red-600 bg-red-50 border-red-200',
  medium: 'text-amber-600 bg-amber-50 border-amber-200',
  low: 'text-stone-500 bg-stone-50 border-stone-200',
};

const SeasonalReminders = ({ isDemo, userPlan, onNavigate }) => {
  const [expanded, setExpanded] = useState(false);
  const [data, setData] = useState(null);
  const [hemisphere, setHemisphere] = useState(() => localStorage.getItem('hemisphere') || 'southern');
  const [toggling, setToggling] = useState(null);
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTask, setNewTask] = useState('');
  const [newPriority, setNewPriority] = useState('medium');
  const [addingTask, setAddingTask] = useState(false);
  const [showNextSeason, setShowNextSeason] = useState(false);
  const [congratsDismissed, setCongratsDismissed] = useState(false);

  const canAddCustomTasks = userPlan === 'association' || userPlan === 'large_club';

  const fetchReminders = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/seasonal-reminders?hemisphere=${hemisphere}`, { headers });
      setData(res.data);
      setCongratsDismissed(false);
      setShowNextSeason(false);
    } catch { /* empty */ }
  }, [hemisphere]);

  useEffect(() => {
    fetchReminders();
  }, [fetchReminders]);

  const toggleTask = async (taskName) => {
    if (isDemo) return;
    setToggling(taskName);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/seasonal-reminders/toggle`, { task: taskName, hemisphere }, { headers: { Authorization: `Bearer ${token}` } });
      setData(prev => ({
        ...prev,
        tasks: prev.tasks.map(t => t.task === taskName ? { ...t, completed: !t.completed } : t),
      }));
    } catch { /* empty */ }
    setToggling(null);
  };

  const addCustomTask = async () => {
    if (!newTask.trim()) return;
    setAddingTask(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/seasonal-tasks/custom`, {
        task: newTask.trim(),
        season: data?.season || 'summer',
        priority: newPriority,
        icon: 'clipboard',
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Task added');
      setNewTask('');
      setShowAddTask(false);
      fetchReminders();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to add task';
      if (err.response?.status === 403) {
        toast.error('Custom tasks require a 30+ hive plan');
      } else {
        toast.error(msg);
      }
    }
    setAddingTask(false);
  };

  const deleteCustomTask = async (taskId, e) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/seasonal-tasks/custom/${taskId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Task removed');
      fetchReminders();
    } catch {
      toast.error('Failed to remove task');
    }
  };

  const switchHemisphere = (h) => {
    setHemisphere(h);
    localStorage.setItem('hemisphere', h);
  };

  if (!data) return null;

  const season = data.season;
  const nextSeason = data.next_season || 'summer';
  const meta = SEASON_META[season] || SEASON_META.summer;
  const nextMeta = SEASON_META[nextSeason] || SEASON_META.summer;
  const SeasonIcon = meta.icon;
  const NextSeasonIcon = nextMeta.icon;
  const completedCount = data.tasks.filter(t => t.completed).length;
  const totalCount = data.tasks.length;
  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const allComplete = totalCount > 0 && completedCount === totalCount;
  const nextTasks = data.next_season_tasks || [];

  return (
    <div className={`${meta.bg} border ${meta.border} rounded-2xl overflow-hidden`} data-testid="seasonal-reminders">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:opacity-90 transition"
        data-testid="seasonal-reminders-toggle"
      >
        <div className="flex items-center gap-3">
          <SeasonIcon className={`h-5 w-5 ${meta.color}`} />
          <div className="text-left">
            <span className={`font-bold text-sm ${meta.color}`}>{meta.label} Checklist</span>
            <span className="text-xs text-stone-500 ml-2">
              {allComplete ? 'Complete!' : `${completedCount}/${totalCount} done`}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {allComplete ? (
            <CheckCircle2 className="h-5 w-5 text-green-500" />
          ) : (
            <div className="w-16 h-1.5 bg-white/60 rounded-full overflow-hidden">
              <div className={`h-full ${meta.accent} rounded-full transition-all`} style={{ width: `${progress}%` }} />
            </div>
          )}
          {expanded ? <ChevronUp className="h-4 w-4 text-stone-400" /> : <ChevronDown className="h-4 w-4 text-stone-400" />}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {/* Hemisphere toggle + Season Settings link */}
          <div className="flex items-center gap-2 pb-2 border-b border-stone-200/50">
            <span className="text-xs text-stone-500">Hemisphere:</span>
            <Toggle
              pressed={hemisphere === 'southern'}
              onPressedChange={() => switchHemisphere('southern')}
              className="h-7 px-3 text-xs data-[state=on]:bg-stone-700 data-[state=on]:text-white"
              data-testid="hemisphere-southern"
            >
              Southern
            </Toggle>
            <Toggle
              pressed={hemisphere === 'northern'}
              onPressedChange={() => switchHemisphere('northern')}
              className="h-7 px-3 text-xs data-[state=on]:bg-stone-700 data-[state=on]:text-white"
              data-testid="hemisphere-northern"
            >
              Northern
            </Toggle>
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-stone-500">
                {data.hive_summary?.active || 0} active hive{data.hive_summary?.active !== 1 ? 's' : ''}
              </span>
              {onNavigate && !isDemo && (
                <button
                  onClick={() => onNavigate('season-settings')}
                  className="text-stone-400 hover:text-stone-600 p-1 rounded-md hover:bg-white/50 transition"
                  title="Season Settings"
                  data-testid="season-settings-link"
                >
                  <Settings className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Season Complete Congrats */}
          {allComplete && !congratsDismissed && (
            <div className="bg-white rounded-xl p-4 border border-green-200 shadow-sm text-center" data-testid="season-complete-congrats">
              <PartyPopper className="h-8 w-8 text-amber-500 mx-auto mb-2" />
              <h3 className="font-bold text-green-700 text-base">{meta.label} Complete!</h3>
              <p className="text-sm text-stone-600 mt-1">
                Well done — all {totalCount} {meta.label.toLowerCase()} tasks are ticked off.
              </p>
              <div className="flex items-center justify-center gap-2 mt-3">
                <Button
                  size="sm"
                  onClick={() => { setShowNextSeason(true); setCongratsDismissed(true); }}
                  className={`${nextMeta.accent} hover:opacity-90 text-white text-xs gap-1`}
                  data-testid="view-next-season-btn"
                >
                  <NextSeasonIcon className="h-3.5 w-3.5" />
                  Preview {nextMeta.label} Checklist
                  <ArrowRight className="h-3.5 w-3.5" />
                </Button>
                <button
                  onClick={() => setCongratsDismissed(true)}
                  className="text-xs text-stone-400 hover:text-stone-600 px-2 py-1"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {/* Current Season Tasks (or Next Season Preview) */}
          {!showNextSeason ? (
            <>
              {/* Tasks */}
              <div className="space-y-1">
                {data.tasks.map((task) => {
                  const TaskIcon = ICON_MAP[task.icon] || Circle;
                  const prioClass = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium;
                  return (
                    <div key={task.task} className="flex items-center">
                      <button
                        onClick={() => toggleTask(task.task)}
                        disabled={isDemo || toggling === task.task}
                        className={`flex-1 flex items-center gap-3 px-3 py-2.5 rounded-xl transition text-left ${
                          task.completed ? 'bg-white/40 opacity-60' : 'bg-white hover:bg-white/80'
                        } ${isDemo ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                        data-testid={`task-${task.task.slice(0, 20).replace(/\s/g, '-').toLowerCase()}`}
                      >
                        {task.completed ? (
                          <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                        ) : (
                          <Circle className="h-5 w-5 text-stone-300 flex-shrink-0" />
                        )}
                        <TaskIcon className={`h-4 w-4 flex-shrink-0 ${task.completed ? 'text-stone-400' : meta.color}`} />
                        <span className={`text-sm flex-1 ${task.completed ? 'line-through text-stone-400' : 'text-stone-700'}`}>
                          {task.task}
                        </span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium flex-shrink-0 ${prioClass}`}>
                          {task.priority}
                        </span>
                        {task.is_custom && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-600 border border-purple-200 font-medium flex-shrink-0 ml-1">
                            custom
                          </span>
                        )}
                      </button>
                      {task.is_custom && task.custom_id && !isDemo && (
                        <button
                          onClick={(e) => deleteCustomTask(task.custom_id, e)}
                          className="ml-1 p-1.5 rounded-lg text-stone-400 hover:text-red-500 hover:bg-red-50 transition"
                          title="Remove custom task"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Add Custom Task */}
              {!isDemo && (
                <div className="pt-2 border-t border-stone-200/50">
                  {!showAddTask ? (
                    <button
                      onClick={() => {
                        if (!canAddCustomTasks) {
                          toast('Custom tasks require a 30+ hive plan', { description: 'Upgrade to Beyond Hobby or Large Club to add your own seasonal tasks.' });
                          return;
                        }
                        setShowAddTask(true);
                      }}
                      className={`flex items-center gap-2 text-xs font-medium px-3 py-2 rounded-lg transition ${
                        canAddCustomTasks
                          ? `${meta.color} hover:bg-white/60`
                          : 'text-stone-400 hover:bg-white/40'
                      }`}
                      data-testid="add-custom-task-btn"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add your own task
                      {!canAddCustomTasks && <Lock className="h-3 w-3 ml-1" />}
                    </button>
                  ) : (
                    <div className="flex items-center gap-2 bg-white rounded-xl p-2" data-testid="add-task-form">
                      <Input
                        value={newTask}
                        onChange={(e) => setNewTask(e.target.value)}
                        placeholder="e.g., Build swarm traps..."
                        className="h-9 text-sm flex-1"
                        onKeyDown={(e) => e.key === 'Enter' && addCustomTask()}
                        autoFocus
                      />
                      <Select value={newPriority} onValueChange={setNewPriority}>
                        <SelectTrigger className="h-9 w-24 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="low">Low</SelectItem>
                        </SelectContent>
                      </Select>
                      <button
                        onClick={addCustomTask}
                        disabled={addingTask || !newTask.trim()}
                        className="h-9 px-3 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 disabled:opacity-50"
                      >
                        {addingTask ? '...' : 'Add'}
                      </button>
                      <button
                        onClick={() => { setShowAddTask(false); setNewTask(''); }}
                        className="h-9 px-2 text-stone-400 hover:text-stone-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            /* Next Season Preview */
            <div data-testid="next-season-preview">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <NextSeasonIcon className={`h-4 w-4 ${nextMeta.color}`} />
                  <span className={`font-bold text-sm ${nextMeta.color}`}>Upcoming: {nextMeta.label}</span>
                  <span className="text-xs text-stone-400">{nextTasks.length} tasks</span>
                </div>
                <button
                  onClick={() => setShowNextSeason(false)}
                  className="text-xs text-stone-500 hover:text-stone-700 flex items-center gap-1"
                  data-testid="back-to-current-btn"
                >
                  Back to {meta.label}
                </button>
              </div>
              <div className="space-y-1">
                {nextTasks.map((task) => {
                  const TaskIcon = ICON_MAP[task.icon] || Circle;
                  const prioClass = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium;
                  return (
                    <div key={task.task} className="flex items-center gap-3 px-3 py-2.5 bg-white/60 rounded-xl">
                      <Circle className="h-5 w-5 text-stone-200 flex-shrink-0" />
                      <TaskIcon className={`h-4 w-4 flex-shrink-0 ${nextMeta.color}`} />
                      <span className="text-sm text-stone-600 flex-1">{task.task}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium flex-shrink-0 ${prioClass}`}>
                        {task.priority}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SeasonalReminders;
